/**
 * Shared standings utilities for both editor and frontend
 */

/**
 * Client-side cache for standings data
 */
const standingsCache = new Map();

/**
 * Format goal difference with proper +/- prefix
 *
 * @param {number} goalsFor     - Goals scored
 * @param {number} goalsAgainst - Goals conceded
 * @return {string} Formatted goal difference
 */
export function formatGoalDifference(goalsFor, goalsAgainst) {
  const diff = (goalsFor || 0) - (goalsAgainst || 0);
  return diff > 0 ? `+${diff}` : String(diff);
}

/**
 * Get team display name
 *
 * @param {Object} team - Team standings data
 * @return {string} Team display name
 */
function getTeamDisplayName(team) {
  return team.contestant?.shortName || team.contestant?.name;
}

/**
 * Generate CSS classes for a standings row
 *
 * @param {Object} team     - Team standings data
 * @param {number} position - Position in table
 * @return {string} CSS classes string
 */
function generateRowClasses(team, position) {
  const actualPosition = team.position || position;
  const rankStatusClass = team.rankStatus
    ? `wp-block-big-bite-stats-perform-standings__row--rank-${team.rankStatus.toLowerCase().replace(/\s+/g, '-')}`
    : '';

  return [
    'wp-block-big-bite-stats-perform-standings__row',
    `wp-block-big-bite-stats-perform-standings__row--position-${actualPosition}`,
    rankStatusClass,
  ]
    .filter(Boolean)
    .join(' ');
}

/**
 * Generate HTML table row for a standings entry
 *
 * @param {Object} team     - Team standings data
 * @param {number} position - Position in table (fallback if not in data)
 * @return {string} HTML table row string
 */
export function generateStandingsRow(team, position) {
  const actualPosition = team.position || position;
  const teamName = getTeamDisplayName(team);
  const goalDiff = formatGoalDifference(team.goalsFor, team.goalsAgainst);
  const rowClasses = generateRowClasses(team, position);

  return `
    <tr class="${rowClasses}">
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--position-indicator" aria-hidden="true"></td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--position">${actualPosition}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--team">
        <span class="wp-block-big-bite-stats-perform-standings__team-name">${teamName}</span>
      </td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--played">${team.matchesPlayed || 0}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--won">${team.matchesWon || 0}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--drawn">${team.matchesDrawn || 0}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--lost">${team.matchesLost || 0}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--goals-for">${team.goalsFor || 0}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--goals-against">${team.goalsAgainst || 0}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--goal-difference">${goalDiff}</td>
      <td class="wp-block-big-bite-stats-perform-standings__cell wp-block-big-bite-stats-perform-standings__cell--points">${team.points || 0}</td>
    </tr>`;
}

/**
 * Generate complete HTML table body for standings
 *
 * @param {Array} standings - Array of team standings data
 * @return {string} Complete HTML table body string
 */
export function generateStandingsTableBody(standings) {
  if (!Array.isArray(standings) || standings.length === 0) {
    return '';
  }

  return standings.map((team, index) => generateStandingsRow(team, index + 1)).join('');
}

/**
 * Validate standings API response
 *
 * @param {Object} response - API response object
 * @return {boolean} Whether response contains valid standings data
 */
export function validateStandingsResponse(response) {
  return (
    response &&
    response.data &&
    response.data.standings &&
    Array.isArray(response.data.standings) &&
    response.data.standings.length > 0
  );
}

/**
 * Extract error message from API response or error object
 *
 * @param {Object|Error} error - Error object or failed response
 * @return {string} User-friendly error message
 */
export function extractErrorMessage(error) {
  if (error.message) {
    return `Failed to load standings: ${error.message}`;
  }

  if (error.status && error.statusText) {
    return `HTTP ${error.status}: ${error.statusText}`;
  }

  return 'Failed to load standings data. Please try again later.';
}

/**
 * Generate cache key for standings data
 *
 * @param {string} tournamentCalendarId - Tournament calendar ID
 * @param {Object} options              - Additional query parameters
 * @return {string} Cache key
 */
export function generateStandingsCacheKey(tournamentCalendarId, options = {}) {
  const keyParts = ['standings', tournamentCalendarId || 'all', JSON.stringify(options)];
  return keyParts.join('_');
}

/**
 * Build API URL for standings endpoint
 *
 * @param {string} tournamentCalendarId - Tournament calendar ID
 * @param {Object} options              - Additional query parameters
 * @return {string} Complete API URL
 */
export function buildStandingsApiUrl(tournamentCalendarId, options = {}) {
  const baseUrl = '/wp-json/big-bite-stats-perform/v1/standings';
  const params = new URLSearchParams();

  if (tournamentCalendarId) {
    params.append('tmcl', tournamentCalendarId);
  }

  // Add any additional parameters
  Object.entries(options).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      params.append(key, value);
    }
  });

  return `${baseUrl}${params.toString() ? `?${params.toString()}` : ''}`;
}

/**
 * Fetch standings data from API with client-side caching
 *
 * @param {string} tournamentCalendarId - Tournament calendar ID
 * @param {Object} options              - Additional options for API call
 * @return {Promise<Object>} API response data or error
 */
export async function fetchStandingsData(tournamentCalendarId, options = {}) {
  if (!tournamentCalendarId) {
    throw new Error('Tournament calendar ID is required');
  }

  // Generate cache key
  const cacheKey = generateStandingsCacheKey(tournamentCalendarId, options);

  // Check cache first
  if (standingsCache.has(cacheKey)) {
    return standingsCache.get(cacheKey);
  }

  const url = buildStandingsApiUrl(tournamentCalendarId, options);

  try {
    const nonce = window.wpApiSettings?.nonce;
    if (!nonce) throw new Error('Security token not available');

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-WP-Nonce': nonce,
      },
    });

    if (!response.ok) {
      // Try to get the actual error message from the API response
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      try {
        const errorData = await response.json();
        if (errorData.message) {
          errorMessage = errorData.message;
        }
      } catch (e) {
        // If we can't parse the error response, use the status text
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();

    if (!validateStandingsResponse(data)) {
      throw new Error('Invalid or empty standings data received');
    }

    // Cache the successful response
    standingsCache.set(cacheKey, data);

    return data;
  } catch (error) {
    const errorMessage = extractErrorMessage(error);

    // Don't cache errors, but throw with formatted message
    throw new Error(errorMessage);
  }
}

/**
 * Update DOM table body with new standings data
 *
 * @param {Array}  standings - Standings data array
 * @param {string} selector  - CSS selector for table body element
 * @return {boolean} Whether update was successful
 */
export function updateStandingsTable(
  standings,
  selector = '.wp-block-big-bite-stats-perform-standings__table tbody',
) {
  const tbodyElement = document.querySelector(selector);

  if (!tbodyElement) {
    return false;
  }

  const tableBodyHtml = generateStandingsTableBody(standings);
  tbodyElement.innerHTML = tableBodyHtml;

  return true;
}

/**
 * Clear standings table content
 *
 * @param {string} selector - CSS selector for table body element
 * @return {boolean} Whether clear was successful
 */
export function clearStandingsTable(
  selector = '.wp-block-big-bite-stats-perform-standings__table tbody',
) {
  const tbodyElement = document.querySelector(selector);

  if (!tbodyElement) {
    return false;
  }

  tbodyElement.innerHTML = '';
  return true;
}

/**
 * Cache management utilities
 */
export const standingsCacheUtils = {
  /**
   * Clear all cached standings data
   */
  clearCache: () => {
    standingsCache.clear();
  },

  /**
   * Clear cache for specific tournament
   *
   * @param {string} tournamentCalendarId - Tournament calendar ID to clear
   */
  clearTournamentCache: (tournamentCalendarId) => {
    if (!tournamentCalendarId) {
      return;
    }

    // Find and delete all cache entries for this tournament
    for (const [key] of standingsCache.entries()) {
      if (key.includes(`_${tournamentCalendarId}_`)) {
        standingsCache.delete(key);
      }
    }
  },

  /**
   * Get cache statistics for debugging
   *
   * @return {Object} Cache statistics
   */
  getCacheStats: () => {
    return {
      size: standingsCache.size,
      keys: [...standingsCache.keys()],
    };
  },

  /**
   * Check if data is cached for specific tournament
   *
   * @param {string} tournamentCalendarId - Tournament calendar ID
   * @param {Object} options              - Options to check
   * @return {boolean} Whether data is cached
   */
  isCached: (tournamentCalendarId, options = {}) => {
    const cacheKey = generateStandingsCacheKey(tournamentCalendarId, options);
    return standingsCache.has(cacheKey);
  },
};

/**
 * Tournament filtering utilities
 */
export const tournamentUtils = {
  /**
   * Filter tournaments to only show those suitable for standings
   * (i.e., exclude knockout competitions)
   *
   * @param {Array} tournaments - Array of tournament objects
   * @return {Array} Filtered tournaments suitable for standings
   */
  filterForStandings: (tournaments) => {
    if (!Array.isArray(tournaments)) {
      return [];
    }

    return tournaments.filter((tournament) => {
      const name = (tournament.name || '').toLowerCase();

      // Exclude knockout-style competitions
      const knockoutKeywords = [
        'cup',
        'champions league',
        'europa league',
        'conference league',
        'knockout',
        'playoff',
        'final',
        'semi',
        'quarter',
      ];

      return !knockoutKeywords.some((keyword) => name.includes(keyword));
    });
  },

  /**
   * Find tournament by ID
   *
   * @param {Array}  tournaments - Array of tournament objects
   * @param {string} id          - Tournament ID to find
   * @return {Object|null} Found tournament object or null
   */
  findById: (tournaments, id) => {
    if (!Array.isArray(tournaments) || !id) {
      return null;
    }

    return tournaments.find((tournament) => tournament.id === id) || null;
  },
};
